import './style.scss';
import { CreateTeamSteps } from '../Components/CreateTeamSteps/CreateTeamSteps';
import {
  TextInput,
  Textarea,
  useToastContext,
  Button,
  Loading,
  useForm,
  TruForm,
  required,
} from '@trufla-technology/trumarket-react-design-system';
import { CreateTeamBody } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket/types';
import { useHistory, useParams } from 'react-router-dom';
import {
  useCreateTeam,
  useUpdateTeamByKey,
} from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { EditTeamSteps } from '../Components/EditTeamSteps/EditTeamSteps';
import { IErrorResponse } from 'app/types/ErrorResponse';
import { useEffect, useState } from 'react';
import { useAuthContext } from '@trufla-technology/trufla-trudomain/dist/lib';
export type props = {
  mode?: string;
  initialData?: CreateTeamBody;
};
type paramTypes = {
  teamKey: string;
};
export const TeamDetails = (props: props) => {
  const { teamKey } = useParams<paramTypes>();

  const { isPermitted } = useAuthContext();
  const history = useHistory<{
    name: string;
    key?: string | undefined;
    description: string;
  }>();

  const { state } = history.location;

  const [initialData, setInitialData] = useState(props.initialData);
  const [loading, setLoading] = useState<boolean>(false);

  const [mode, setMode] = useState<string>(props.mode ?? '');
  useEffect(() => {
    setInitialData(props.initialData);
  }, [props.initialData]);

  useEffect(() => {
    if (state) {
      setMode(props.mode!! ?? 'edit');
      setInitialData({ name: state.name, description: state.description });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createCreateTeam = useCreateTeam();
  const updateTeamByKey = useUpdateTeamByKey();
  const form = useForm<CreateTeamBody>(
    initialData || {
      name: history.location.state?.name,
      description: history.location.state?.description,
    },
  );
  const toaster = useToastContext();

  const handleFinish = async (formSubmitted: Partial<CreateTeamBody>) => {
    try {
      if (mode === 'edit' && initialData) {
        if (
          initialData!!.name !== formSubmitted.name ||
          initialData!!.description !== formSubmitted.description
        ) {
          await updateTeamByKey(teamKey || state.key!!, {
            name:
              initialData.name !== formSubmitted.name
                ? formSubmitted.name
                : undefined,
            description:
              initialData.description !== formSubmitted.description
                ? formSubmitted.description
                : undefined,
          });
        }
        state
          ? history.push(
              `/configurations/teams/team-permissions/create/${state.key}`,
              {
                name: formSubmitted.name!!,
                description: formSubmitted.description!!,
              },
            )
          : history.push(`/configurations/teams/team-users/${teamKey}`);

        toaster.success('Successfully, updated team');
      } else {
        if (!state) {
          setLoading(true);
          const res = await createCreateTeam({
            name: formSubmitted.name as string,
            description: formSubmitted.description,
          });
          if (res.data) {
            setLoading(false);
            toaster.success('Team created successfully');
            history.push(
              `/configurations/teams/team-permissions/create/${res.data.key}`,
              {
                name: formSubmitted.name!!,
                description: formSubmitted.description!!,
              },
            );
          }
        } else {
          history.push(
            `/configurations/teams/team-permissions/create/${state.key}`,
            {
              name: formSubmitted.name!!,
              description: formSubmitted.description!!,
            },
          );
        }
      }
    } catch (err: unknown) {
      setLoading(false);
      const errorMessage = (
        err as IErrorResponse
      )?.response?.data?.error.replaceAll('body.', '');
      toaster.error(errorMessage || 'Failed to add/update a team');
    }
  };
  useEffect(() => form.reset(), [form, initialData]);

  return (
    <div className="teams-details">
      <div className="form">
        <TruForm onFinish={handleFinish} form={form}>
          {props.mode === 'edit' ? (
            <EditTeamSteps />
          ) : (
            <CreateTeamSteps step={1} loading={loading} />
          )}
          {props.mode === 'edit' && !props.initialData && (
            <div className="loading-team-details">
              <Loading />
            </div>
          )}
          {((props.mode === 'edit' && props.initialData) ||
            props.mode !== 'edit') && (
            <div className="details">
              <p className="form-title">Team Details</p>
              <div
                className={`details-input ${loading ? 'disabled-details' : ''}`}
              >
                <form.Item name="name" label="Name" rules={[required]}>
                  <TextInput disabled={!isPermitted('updateTeamByKey')} />
                </form.Item>
                <form.Item
                  name="description"
                  label="Description"
                  className="description-field"
                  rules={[
                    required,
                    { max: 255, message: 'Must be less than 255 chars' },
                  ]}
                >
                  <Textarea disabled={!isPermitted('updateTeamByKey')} />
                </form.Item>
              </div>
              {props.mode === 'edit' && (
                <div className="controls">
                  <Button
                    disabled={!isPermitted('updateTeamByKey')}
                    htmlType="submit"
                    type="primary"
                  >
                    Save
                  </Button>
                  <Button
                    classes="cancel-create-team"
                    htmlType="button"
                    onClick={() => {
                      history.replace({ search: '' });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}
        </TruForm>
      </div>
    </div>
  );
};
